const MODULE_NAME = "errorHandler";

/**
 * Friendly error messages for users
 * Detailed errors logged to console for debugging
 */
export const errorMessages = {
    NETWORK_ERROR: "Unable to connect to the server. Please check your internet connection and try again.",
    AUTH_ERROR: "Your session has expired. Please log in again.",
    VALIDATION_ERROR: "Please check your input and try again.",
    SERVER_ERROR: "Something went wrong on our end. Please try again later.",
    NOT_FOUND: "The requested data could not be found.",
    PERMISSION_ERROR: "You don't have permission to perform this action.",
    DEFAULT: "We are sorry. Some unexpected error occurred. Please try again."
};

/**
 * Handle API errors with friendly user messages
 * @param {Error} error - The error object
 * @param {string} context - Where the error occurred (for logging)
 * @returns {string} User-friendly error message
 */
export const handleApiError = (error, context = "API Call") => {
    const detailedError = {
        timestamp: new Date().toISOString(),
        context,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
    };

    console.error(`[${MODULE_NAME}] ${context}:`, detailedError);

    // Network errors (no response from server)
    if (error.code === "ERR_NETWORK" || !error.response) {
        return errorMessages.NETWORK_ERROR;
    }

    // Handle specific HTTP status codes
    const status = error.response?.status;
    switch (status) {
        case 401:
        case 403:
            return errorMessages.AUTH_ERROR;
        case 404:
            return errorMessages.NOT_FOUND;
        case 400:
            return errorMessages.VALIDATION_ERROR;
        case 403:
            return errorMessages.PERMISSION_ERROR;
        case 500:
        case 502:
        case 503:
            return errorMessages.SERVER_ERROR;
        default:
            return errorMessages.DEFAULT;
    }
};

/**
 * Wrap async API calls with error handling
 * @param {Function} apiCall - The async API call function
 * @param {string} context - Context for error logging
 * @param {Function} onError - Optional callback to handle errors
 * @returns {Promise}
 */
export const withErrorHandling = async (apiCall, context = "API Call", onError = null) => {
    try {
        return await apiCall();
    } catch (error) {
        const friendlyMessage = handleApiError(error, context);
        if (onError) {
            onError(friendlyMessage);
        }
        throw error;
    }
};